if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        // GlowMeds lightbox navigation. Only active when the nav buttons exist
        // (snippets/product-media-modal.liquid); otherwise the theme default applies.
        this.navButtons = this.querySelectorAll('[data-gm-nav]');
        if (!this.navButtons.length) return;

        this.setAttribute('data-gm-lightbox', '');
        this.counterCurrent = this.querySelector('[data-gm-current]');
        this.counterTotal = this.querySelector('[data-gm-total]');
        this.lastWheelAt = 0;
        this.lastWheelNavAt = 0;

        this.navButtons.forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            this.step(button.dataset.gmNav === 'next' ? 1 : -1);
          });
          // ModalDialog closes the media modal on any mouse pointerup; keep it open for nav clicks.
          button.addEventListener('pointerup', (event) => event.stopPropagation());
        });

        this.addEventListener('keydown', this.onKeyDown.bind(this));
        this.addEventListener('wheel', this.onWheel.bind(this), { passive: false });
        this.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
        this.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });
      }

      hide() {
        const active = this.hasAttribute('data-gm-lightbox')
          ? this.querySelector('.product-media-modal__content > .active[data-media-id]')
          : null;
        const openedMediaId = this.openedBy?.getAttribute('data-media-id');
        super.hide();
        // Show the image the customer finished on in the main gallery.
        if (active && openedMediaId && active.dataset.mediaId !== openedMediaId) {
          this.syncGallery(active.dataset.mediaId);
        }
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
        if (this.hasAttribute('data-gm-lightbox')) this.updateCounter();
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      /* ---------- GlowMeds lightbox navigation ---------- */

      // Same set of media the gallery shows: skips items hidden by "Hide other variants' media"
      // (except the featured one, which is always first) and anything with the hidden attribute.
      getNavigableMedia() {
        const content = this.querySelector('.product-media-modal__content');
        if (!content) return [];
        const first = content.firstElementChild;
        return Array.from(content.children).filter(
          (element) =>
            element.hasAttribute('data-media-id') &&
            !element.hidden &&
            !(element.classList.contains('product__media-item--variant') && element !== first)
        );
      }

      step(direction) {
        const items = this.getNavigableMedia();
        if (items.length < 2) return;
        let index = items.findIndex((element) => element.classList.contains('active'));
        if (index === -1) index = 0;
        this.showMediaElement(items[(index + direction + items.length) % items.length]);
      }

      showMediaElement(element) {
        window.pauseAllMedia();
        this.querySelectorAll('.product-media-modal__content > [data-media-id]').forEach((item) =>
          item.classList.remove('active')
        );
        element.classList.add('active');

        const template = element.querySelector('template');
        if (element.nodeName === 'DEFERRED-MEDIA' && template && template.content.querySelector('.js-youtube')) {
          element.loadContent();
        }
        this.updateCounter();
      }

      updateCounter() {
        const items = this.getNavigableMedia();
        const index = items.findIndex((element) => element.classList.contains('active'));
        this.toggleAttribute('data-gm-single', items.length < 2);
        if (this.counterCurrent) this.counterCurrent.textContent = index === -1 ? 1 : index + 1;
        if (this.counterTotal) this.counterTotal.textContent = items.length;
      }

      onKeyDown(event) {
        if (!this.hasAttribute('open')) return;
        if (event.target.closest('video, model-viewer, input, textarea, select')) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.step(-1);
        } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          this.step(1);
        }
      }

      // Wheel up = previous, wheel down = next. One image per gesture (trackpads send
      // a long stream of wheel events, so wait for a short pause or 800 ms).
      onWheel(event) {
        if (!this.hasAttribute('open')) return;
        if (event.ctrlKey || event.target.closest('product-model, model-viewer')) return;
        event.preventDefault();

        const delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
        const now = Date.now();
        const isNewGesture = now - this.lastWheelAt > 250;
        this.lastWheelAt = now;

        if (Math.abs(delta) < 4) return;
        if (!isNewGesture && now - this.lastWheelNavAt < 800) return;
        this.lastWheelNavAt = now;
        this.step(delta > 0 ? 1 : -1);
      }

      onTouchStart(event) {
        if (event.touches.length !== 1) {
          this.touchStart = null;
          return;
        }
        this.touchStart = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      }

      // Swipe left/up = next, swipe right/down = previous. Ignored while pinch-zoomed.
      onTouchEnd(event) {
        if (!this.touchStart || !this.hasAttribute('open')) return;
        const touch = event.changedTouches[0];
        const dx = touch.clientX - this.touchStart.x;
        const dy = touch.clientY - this.touchStart.y;
        this.touchStart = null;

        if (event.target.closest('[data-gm-nav], product-model, model-viewer')) return;
        if (window.visualViewport && window.visualViewport.scale > 1.01) return;
        if (Math.max(Math.abs(dx), Math.abs(dy)) < 50) return;

        if (Math.abs(dx) > Math.abs(dy)) this.step(dx < 0 ? 1 : -1);
        else this.step(dy < 0 ? 1 : -1);
      }

      syncGallery(mediaId) {
        const gallery = document.getElementById(`MediaGallery-${this.dataset.section}`);
        if (!gallery || typeof gallery.setActiveMedia !== 'function') return;
        gallery.setActiveMedia(`${this.dataset.section}-${mediaId}`, false);
        gallery.querySelector(`.product__media-toggle[data-media-id="${mediaId}"]`)?.focus({ preventScroll: true });
      }
    }
  );
}
